import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { trackerUrls } from '@/tracker/config/urls';
import { isClosedStatus } from '@/tracker/entities/status';
import { useIssueDrawer } from '@/tracker/features/issueDrawer';
import { TaskBasic } from '@/tracker/types';
import { ExpandIcon } from '@/tracker/ui/ExpandIcon';

import { COLUMNS, IssueColumn, IssueSort } from '../config';
import { groupIssues } from '../groupIssues';
import { IssueListSettings } from '../useIssueListSettings';

import { firstDirection } from './firstDirection';

import s from './IssueList.module.scss';

type Props = {
  issues: TaskBasic[];
  settings: IssueListSettings;
  sort: IssueSort;
  onSortChange: (sort: IssueSort) => void;
  /** Идёт загрузка поверх показанных строк — приглушаем таблицу */
  isLoading?: boolean;
};

/** Список задач как в Яндекс Трекере: компактные строки, настраиваемые колонки, группы */
const IssueList: React.FC<Props> = observer(
  ({ issues, settings, sort, onSortChange, isLoading = false }) => {
    const { openIssue, issueLink } = useIssueDrawer();
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

    const columns = COLUMNS.filter(
      (column) => column.isRequired || settings.columns.includes(column.id),
    );
    const groups = groupIssues({ issues, groupBy: settings.groupBy });
    const isGrouped = settings.groupBy !== 'none';

    // id групп разных группировок совпадают («Не назначен» и весь список без группировки — '')
    const collapseKey = (groupId: string) => `${settings.groupBy}:${groupId}`;

    const toggleGroup = (groupId: string) =>
      setCollapsed((prev) => {
        const next = new Set(prev);
        const key = collapseKey(groupId);

        if (!next.delete(key)) next.add(key);

        return next;
      });

    const changeSort = (field: IssueSort['field']) => {
      if (sort.field === field) {
        onSortChange({ field, direction: sort.direction === 'asc' ? 'desc' : 'asc' });

        return;
      }

      onSortChange({ field, direction: firstDirection(field) });
    };

    const renderCell = (column: IssueColumn, issue: TaskBasic) => {
      if (column.id === 'key') {
        return (
          <Link
            to={issueLink(issue.key)}
            className={cn(s.key, isClosedStatus(issue.status) && s.key_closed)}
            onClick={(event) => event.stopPropagation()}
          >
            {issue.key}
          </Link>
        );
      }

      if (column.id === 'summary') {
        return (
          <>
            <div className={s.summary}>{issue.summary}</div>
            {issue.parent && (
              <div className={s.parent}>
                {issue.parent.key} {issue.parent.summary}
              </div>
            )}
          </>
        );
      }

      if (column.id === 'queue') {
        return (
          <Link
            to={trackerUrls.queue(issue.queue.key)}
            className={s.queue}
            onClick={(event) => event.stopPropagation()}
          >
            {issue.queue.name}
          </Link>
        );
      }

      return column.render?.(issue);
    };

    return (
      <Table size="small" stickyHeader className={cn(s.table, isLoading && s.table_loading)}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.id}
                className={cn(column.isIcon && s.cell_icon)}
                sortDirection={sort.field === column.sortField ? sort.direction : false}
                aria-label={column.isIcon ? column.label : undefined}
              >
                {column.sortField ? (
                  <TableSortLabel
                    active={sort.field === column.sortField}
                    direction={sort.field === column.sortField ? sort.direction : 'asc'}
                    onClick={() => changeSort(column.sortField!)}
                    title={column.isIcon ? column.label : undefined}
                  >
                    {column.isIcon ? null : column.label}
                  </TableSortLabel>
                ) : (
                  !column.isIcon && column.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.flatMap((group) => {
            const isCollapsed = isGrouped && collapsed.has(collapseKey(group.id));
            const header = isGrouped && (
              <TableRow key={`group-${group.id}`} className={s.group}>
                <TableCell colSpan={columns.length} className={s.group__cell}>
                  <IconButton
                    size="small"
                    aria-label={isCollapsed ? 'Развернуть группу' : 'Свернуть группу'}
                    aria-expanded={!isCollapsed}
                    onClick={() => toggleGroup(group.id)}
                  >
                    <ExpandIcon isOpen={!isCollapsed} isTree />
                  </IconButton>
                  <span className={s.group__title}>{group.title}</span>
                  <span className={s.group__count}>{group.issues.length}</span>
                </TableCell>
              </TableRow>
            );

            const rows = isCollapsed
              ? []
              : group.issues.map((issue) => (
                  <TableRow
                    key={issue.id}
                    hover
                    className={s.row}
                    onClick={() => openIssue(issue.key)}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        className={cn(s.cell, s[`cell_${column.id}`], column.isIcon && s.cell_icon)}
                      >
                        {renderCell(column, issue)}
                      </TableCell>
                    ))}
                  </TableRow>
                ));

            return header ? [header, ...rows] : rows;
          })}
        </TableBody>
      </Table>
    );
  },
);

export { IssueList };
